import {
  CheckCircle2,
  Database,
  Fingerprint,
  Info,
  Table,
  Tags,
  XCircle,
} from "lucide-react"
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../../internal/primitives/accordion"
import { Input } from "../../internal/primitives/input"
import { Label } from "../../internal/primitives/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../internal/primitives/select"
import { Separator } from "../../internal/primitives/separator"
import type {
  LayerExperience,
  LayerExperienceSourceFieldDefinition,
} from "../../types/layer-experience"
import { cn } from "../../lib/utils"

export interface SourceInformationSectionProps {
  enrichments?: LayerExperience["enrichments"]
  onUpdate: (patch: Partial<LayerExperience["enrichments"]>) => void
  availableFields: Array<{ name: string; type: string }>
}

export function SourceInformationSection({ enrichments, onUpdate, availableFields }: SourceInformationSectionProps) {
  const sourceDef = enrichments?.source_definition
  const fields = sourceDef?.fields || []
  const capabilities = sourceDef?.capabilities || {}

  return (
    <div className="flex flex-col gap-8 pb-10">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-0.5">
          <Label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground/70">Source Identity</Label>
        </div>

        <div className="grid gap-4 rounded-lg border bg-muted/5 p-4 shadow-sm">
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label className="flex items-center gap-1.5 text-[10px] font-bold uppercase text-muted-foreground/60">
                <Fingerprint className="h-3 w-3" />
                Entity Type
              </Label>
              <Input
                value={enrichments?.entity_type || ""}
                onChange={event => onUpdate({ entity_type: event.target.value })}
                placeholder="e.g. Asset"
                className="h-8 text-xs"
              />
            </div>
            <div className="grid gap-2">
              <Label className="flex items-center gap-1.5 text-[10px] font-bold uppercase text-muted-foreground/60">
                <Database className="h-3 w-3" />
                Feature ID Field
              </Label>
              <Select
                value={sourceDef?.identity?.feature_id_field || ""}
                onValueChange={value =>
                  onUpdate({
                    source_definition: sourceDef
                      ? {
                          ...sourceDef,
                          identity: { ...sourceDef.identity, feature_id_field: value },
                        }
                      : {
                          source_id: "unknown",
                          source_layer: "unknown",
                          identity: { feature_id_field: value },
                          fields: [],
                          capabilities: {},
                        },
                  })
                }
              >
                <SelectTrigger className="h-8 bg-background font-mono text-xs">
                  <SelectValue placeholder="Primary key..." />
                </SelectTrigger>
                <SelectContent>
                  {availableFields.map(field => (
                    <SelectItem key={field.name} value={field.name}>
                      {field.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {sourceDef?.geometry_kind ? (
            <div className="flex items-center gap-2 rounded border bg-background p-2 text-[10px] font-medium">
              <span className="text-muted-foreground">Geometry Kind:</span>
              <span className="font-bold uppercase text-primary">{sourceDef.geometry_kind}</span>
            </div>
          ) : null}
        </div>
      </div>

      <Separator />

      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <div className="flex flex-col gap-0.5">
            <Label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground/70">Field Definitions</Label>
          </div>
          <span className="rounded border bg-muted px-2 py-0.5 font-mono text-[10px] text-muted-foreground">
            {fields.length} Fields
          </span>
        </div>

        <Accordion type="multiple" className="w-full space-y-2">
          {fields.map(field => (
            <FieldAccordionItem key={field.field_key} field={field} />
          ))}
          {fields.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-lg border border-dashed bg-muted/10 p-8 text-center">
              <Table className="mb-2 h-6 w-6 text-muted-foreground/30" />
              <p className="text-[10px] font-medium italic text-muted-foreground">No fields defined in source schema.</p>
            </div>
          ) : null}
        </Accordion>
      </div>

      <Separator />

      {sourceDef?.semantic_roles && Object.keys(sourceDef.semantic_roles).length > 0 ? (
        <>
          <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-0.5">
            <Label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground/70">Semantic Roles</Label>
          </div>
            <div className="grid gap-2">
              {Object.entries(sourceDef.semantic_roles).map(([role, field]) => (
                <div key={role} className="flex items-center justify-between rounded-lg border bg-card p-2.5 text-[10px] shadow-sm">
                  <div className="flex flex-col gap-0.5">
                    <span className="text-[9px] font-bold uppercase tracking-tight text-muted-foreground">{role}</span>
                    <span className="font-mono text-[11px] font-semibold text-primary">{field}</span>
                  </div>
                  <div className="rounded-full border border-primary/10 bg-primary/5 p-1.5 text-primary">
                    <Tags className="h-3 w-3" />
                  </div>
                </div>
              ))}
            </div>
          </div>
          <Separator />
        </>
      ) : null}

      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-0.5">
          <Label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground/70">Source Capabilities</Label>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <CapabilityBadge label="Selectable" enabled={capabilities.selectable} />
          <CapabilityBadge label="Hoverable" enabled={capabilities.hoverable} />
          <CapabilityBadge label="Popupable" enabled={capabilities.popupable} />
          <CapabilityBadge label="Filterable" enabled={capabilities.filterable} />
          <CapabilityBadge label="Highlightable" enabled={capabilities.highlightable} />
          <CapabilityBadge label="Spatial Query" enabled={capabilities.spatial_query_mode && capabilities.spatial_query_mode !== "none"} />
        </div>
      </div>

      <Separator />
    </div>
  )
}

function FieldAccordionItem({ field }: { field: LayerExperienceSourceFieldDefinition }) {
  return (
    <AccordionItem value={field.field_key} className="overflow-hidden rounded-lg border bg-card shadow-sm">
      <AccordionTrigger className="px-3 py-2.5 hover:no-underline">
        <div className="flex items-center gap-2 text-left">
          <div className="rounded border bg-muted/50 p-1.5">
            <Database className="h-3.5 w-3.5 text-muted-foreground" />
          </div>
          <div className="flex flex-col">
            <span className="text-[11px] font-bold">{field.label || field.field_key}</span>
            <span className="font-mono text-[9px] uppercase tracking-tight text-muted-foreground">
              {field.value_type} • {field.source_kind.replace("_", " ")}
            </span>
          </div>
        </div>
      </AccordionTrigger>
      <AccordionContent className="space-y-3 px-3 pb-3">
        {field.description ? (
          <div className="flex gap-2 rounded border border-primary/10 bg-primary/5 p-2">
            <Info className="mt-0.5 h-3 w-3 shrink-0 text-primary" />
            <p className="text-[10px] italic leading-relaxed text-muted-foreground">{field.description}</p>
          </div>
        ) : null}

        <div className="grid grid-cols-2 gap-x-4 gap-y-2">
          <div className="space-y-1">
            <Label className="text-[9px] font-bold uppercase tracking-tight text-muted-foreground/60">Technical Key</Label>
            <p className="truncate rounded border bg-muted/30 px-1.5 py-0.5 font-mono text-[10px]">{field.field_key}</p>
          </div>
          <div className="space-y-1">
            <Label className="text-[9px] font-bold uppercase tracking-tight text-muted-foreground/60">Display Format</Label>
            <p className="truncate text-[10px] font-medium">{field.display_format || "Standard / None"}</p>
          </div>
        </div>

        {field.semantic_roles && field.semantic_roles.length > 0 ? (
          <div className="space-y-1">
            <Label className="flex items-center gap-1 text-[9px] font-bold uppercase tracking-tight text-muted-foreground/60">
              <Tags className="h-2.5 w-2.5" />
              Logical Roles
            </Label>
            <div className="flex flex-wrap gap-1">
              {field.semantic_roles.map(role => (
                <span
                  key={role}
                  className="rounded-full border border-primary/20 bg-primary/10 px-2 py-0.5 text-[9px] font-bold uppercase tracking-tighter text-primary"
                >
                  {role}
                </span>
              ))}
            </div>
          </div>
        ) : null}

        <div className="flex items-center gap-4 pt-1">
          <FieldFlag label="Filter Safe" enabled={field.filter_safe} />
          <FieldFlag label="Interact Safe" enabled={field.interaction_safe} />
          <FieldFlag label="Sortable" enabled={field.sortable} />
        </div>
      </AccordionContent>
    </AccordionItem>
  )
}

function CapabilityBadge({ label, enabled }: { label: string; enabled?: boolean | string | null }) {
  return (
    <div
      className={cn(
        "flex items-center justify-between rounded-lg border px-3 py-2 text-[10px] font-bold shadow-sm transition-all",
        enabled ? "border-primary/20 bg-primary/5 text-foreground" : "border-border bg-muted/30 text-muted-foreground opacity-60",
      )}
    >
      <span>{label}</span>
      {enabled ? <CheckCircle2 className="h-3 w-3 text-primary shadow-sm" /> : <XCircle className="h-3 w-3 text-muted-foreground/30" />}
    </div>
  )
}

function FieldFlag({ label, enabled }: { label: string; enabled?: boolean | null }) {
  return (
    <div className="flex items-center gap-1.5">
      <div
        className={cn(
          "h-1.5 w-1.5 rounded-full ring-1 ring-offset-1 ring-offset-background",
          enabled ? "bg-primary ring-primary/30" : "bg-muted-foreground/20 ring-transparent",
        )}
      />
      <span className={cn("text-[9px] font-bold uppercase tracking-tight", enabled ? "text-foreground" : "text-muted-foreground/50")}>
        {label}
      </span>
    </div>
  )
}
